import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Check, X } from 'lucide-react';
import SeoHead from '../../components/SeoHead';

type Cell = { type: 'yes' | 'no' | 'text'; value?: string };
type Row = { featureKey: string; starter: Cell; pro: Cell; enterprise: Cell };
type Section = { sectionKey: string; rows: Row[] };

const SECTIONS: Section[] = [
  {
    sectionKey: 'compare.sectionApps',
    rows: [
      { featureKey: 'compare.featAppLimit', starter: { type: 'text', value: 'compare.limit1' }, pro: { type: 'text', value: 'compare.limit10' }, enterprise: { type: 'text', value: 'compare.limitCustom' } },
    ],
  },
  {
    sectionKey: 'compare.sectionRender',
    rows: [
      { featureKey: 'compare.featRenderWatermarks', starter: { type: 'yes' }, pro: { type: 'no' }, enterprise: { type: 'no' } },
      { featureKey: 'compare.featTemplates', starter: { type: 'text', value: 'compare.templatesBasic' }, pro: { type: 'text', value: 'compare.templatesAdvanced' }, enterprise: { type: 'text', value: 'compare.templatesCustom' } },
    ],
  },
  {
    sectionKey: 'compare.sectionAi',
    rows: [
      { featureKey: 'compare.featAiTranslation', starter: { type: 'text', value: 'compare.aiTranslationNone' }, pro: { type: 'text', value: 'compare.aiTranslationCount' }, enterprise: { type: 'text', value: 'compare.aiTranslationCount' } },
      { featureKey: 'compare.featAiSuggestions', starter: { type: 'no' }, pro: { type: 'yes' }, enterprise: { type: 'yes' } },
      { featureKey: 'compare.featAutoUpload', starter: { type: 'no' }, pro: { type: 'yes' }, enterprise: { type: 'yes' } },
      { featureKey: 'compare.featPriorityQueue', starter: { type: 'no' }, pro: { type: 'yes' }, enterprise: { type: 'yes' } },
    ],
  },
  {
    sectionKey: 'compare.sectionSupport',
    rows: [
      { featureKey: 'compare.featSupport', starter: { type: 'text', value: 'compare.supportCommunity' }, pro: { type: 'text', value: 'compare.supportEmail' }, enterprise: { type: 'text', value: 'compare.supportDedicated' } },
    ],
  },
];

export default function Compare() {
  const { t } = useTranslation();

  function renderCell(cell: Cell) {
    if (cell.type === 'yes') return <Check className="h-5 w-5 text-primary-600 dark:text-primary-400 mx-auto" aria-hidden />;
    if (cell.type === 'no') return <X className="h-5 w-5 text-gray-400 dark:text-gray-500 mx-auto" aria-hidden />;
    return <span className="text-sm text-gray-700 dark:text-gray-300">{cell.value ? t(cell.value) : ''}</span>;
  }

  return (
    <div className="overflow-x-hidden">
      <SeoHead page="compare" />
      {/* Hero — AppScreens-style: one clear headline + subtitle + back link */}
      <section className="relative px-4 sm:px-6 pt-12 sm:pt-20 pb-16 sm:pb-24 bg-gradient-to-b from-primary-50/80 to-white dark:from-gray-900 dark:to-gray-900">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(37,99,235,0.12),transparent)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(59,130,246,0.1),transparent)] pointer-events-none" />
        <div className="relative max-w-3xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white tracking-tight">
            {t('compare.title')}
          </h1>
          <p className="mt-4 sm:mt-5 text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            {t('compare.subtitle')}
          </p>
          <div className="mt-6">
            <Link
              to="/#pricing"
              className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:underline font-medium"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('compare.backToPricing')}
            </Link>
          </div>
        </div>
      </section>

      {/* Grouped comparison tables — AppScreens layout: section heading + table per group */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto space-y-14 sm:space-y-16">
          {SECTIONS.map((section) => (
            <div key={section.sectionKey}>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6">
                {t(section.sectionKey)}
              </h2>
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                        <th className="py-4 px-4 sm:px-6 font-semibold text-gray-900 dark:text-white w-1/3 sm:w-2/5">
                          {t('compare.featureLabel')}
                        </th>
                        <th className="py-4 px-4 text-center font-semibold text-gray-900 dark:text-white min-w-[100px]">
                          {t('compare.starter')}
                        </th>
                        <th className="py-4 px-4 text-center font-semibold text-primary-600 dark:text-primary-400 min-w-[100px] bg-primary-50/30 dark:bg-primary-900/10">
                          {t('compare.pro')}
                        </th>
                        <th className="py-4 px-4 text-center font-semibold text-gray-900 dark:text-white min-w-[100px]">
                          {t('compare.enterprise')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.rows.map((row, idx) => (
                        <tr
                          key={row.featureKey}
                          className={`border-b border-gray-100 dark:border-gray-800 last:border-b-0 ${idx % 2 === 1 ? 'bg-gray-50/50 dark:bg-gray-800/30' : ''}`}
                        >
                          <td className="py-3 px-4 sm:px-6 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t(row.featureKey)}
                          </td>
                          <td className="py-3 px-4 text-center">{renderCell(row.starter)}</td>
                          <td className="py-3 px-4 text-center bg-primary-50/20 dark:bg-primary-900/5">{renderCell(row.pro)}</td>
                          <td className="py-3 px-4 text-center">{renderCell(row.enterprise)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA — AppScreens-style strip */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50 dark:bg-gray-800/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {t('compare.ctaBottomTitle')}
          </h2>
          <p className="mt-3 text-gray-600 dark:text-gray-400">
            {t('compare.ctaBottomSubtitle')}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="w-full sm:w-auto py-3 px-6 rounded-xl font-medium text-center border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 transition-colors"
            >
              {t('compare.ctaStarter')}
            </Link>
            <Link
              to="/register?plan=pro"
              className="w-full sm:w-auto py-3 px-6 rounded-xl font-medium text-center btn btn-primary"
            >
              {t('compare.ctaPro')}
            </Link>
            <Link
              to="/register?plan=enterprise"
              className="w-full sm:w-auto py-3 px-6 rounded-xl font-medium text-center border-2 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-800 transition-colors"
            >
              {t('compare.ctaEnterprise')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
